import { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { CaretDownFilled } from '@ant-design/icons';
import { useAssetListConfig, useAssetListEditorOptions } from '../../../../../hooks/useAssetListConfig';
import { getAssetManagerErrorMessage } from '../../../../../services/AssetManager/assetManager.service';
import { assetManagerServiceMessages } from '../../../../../services/AssetManager/assetManager.constants';
import Notification from '../../../../../shared/components/Notification';
import ConditionalRender from '../../../../../shared/components/ConditionalRender';
import { NotificationTypes } from '../../../../../enums/notificationTypes';
import type { AssetListConfig } from '../../../../../types/InfrastructureManagerTypes';
import styles from './EditAssetListModal.module.scss';

interface EditAssetListModalProps {
  config: AssetListConfig;
  onClose: () => void;
}

interface IncludeRowState {
  id: string;
  layerId: number | null;
  displayField: string;
  // Only used when the chosen layer has no key field yet.
  keyField: string;
}

const createEmptyRow = (): IncludeRowState => ({ id: crypto.randomUUID(), layerId: null, displayField: '', keyField: '' });

// Mounted per open (the popover destroys it on hide), so it always starts from the saved config.
const seedRows = ({ rows }: AssetListConfig): IncludeRowState[] =>
  rows.length ? rows.map(({ layerId, displayField }) => ({ id: crypto.randomUUID(), layerId, displayField, keyField: '' })) : [createEmptyRow()];

const EditAssetListModal = ({ config, onClose }: EditAssetListModalProps) => {
  const [rows, setRows] = useState(() => seedRows(config));
  const { saveConfig, isSavingConfig, setKeyField, isSettingKeyField } = useAssetListConfig();

  const selectedLayerIds = useMemo(() => [...new Set(rows.flatMap(({ layerId }) => (layerId === null ? [] : [layerId])))], [rows]);
  const { layers, isLoadingLayers, layersError, fieldsByLayerId, fieldsError } = useAssetListEditorOptions(selectedLayerIds);

  // Layers picked here that still need a key field before they can hold assets.
  const keylessLayerIds = useMemo(() => new Set((layers ?? []).filter(({ canBeAsset }) => !canBeAsset).map(({ id }) => id)), [layers]);
  const needsKeyField = (row: IncludeRowState) => row.layerId !== null && keylessLayerIds.has(row.layerId);

  // Pre-select the API's suggested key once a keyless layer's fields load; never over a choice already made.
  useEffect(() => {
    setRows((current) => {
      let changed = false;
      const next = current.map((row) => {
        if (row.keyField || row.layerId === null || !keylessLayerIds.has(row.layerId)) return row;
        const suggested = fieldsByLayerId.get(row.layerId)?.suggestedKeyField;
        if (!suggested) return row;
        changed = true;
        return { ...row, keyField: suggested };
      });
      return changed ? next : current;
    });
  }, [fieldsByLayerId, keylessLayerIds]);

  useEffect(() => {
    if (!layersError) return;
    Notification({
      message: assetManagerServiceMessages.LIST_CONFIG.LAYERS_ERROR,
      description: getAssetManagerErrorMessage(layersError),
      type: NotificationTypes.ERROR,
    });
  }, [layersError]);

  useEffect(() => {
    if (!fieldsError) return;
    Notification({
      message: assetManagerServiceMessages.LIST_CONFIG.FIELDS_ERROR,
      description: getAssetManagerErrorMessage(fieldsError),
      type: NotificationTypes.ERROR,
    });
  }, [fieldsError]);

  const handleLayerChange = useCallback((rowId: string, layerId: number | null) => {
    setRows((current) => current.map((row) => (row.id === rowId ? { ...row, layerId, displayField: '', keyField: '' } : row)));
  }, []);

  const handleKeyFieldChange = useCallback((rowId: string, keyField: string) => {
    setRows((current) => current.map((row) => (row.id === rowId ? { ...row, keyField } : row)));
  }, []);

  const handleFieldChange = useCallback((rowId: string, displayField: string) => {
    setRows((current) => current.map((row) => (row.id === rowId ? { ...row, displayField } : row)));
  }, []);

  const handleAddRow = useCallback(() => setRows((current) => [...current, createEmptyRow()]), []);

  // The last row is reset rather than removed, so the editor always keeps one row to fill in.
  const handleRemoveRow = useCallback(
    (rowId: string) => setRows((current) => (current.length > 1 ? current.filter((row) => row.id !== rowId) : [createEmptyRow()])),
    [],
  );

  // A single untouched row clears the list; otherwise every row needs both a layer and a field.
  const isClearing = rows.length === 1 && rows[0].layerId === null;
  const isComplete = rows.every((row) => row.layerId !== null && row.displayField && (!needsKeyField(row) || row.keyField));
  const isSaving = isSavingConfig || isSettingKeyField;
  const canSave = !isSaving && (isClearing || isComplete);

  const handleSave = async () => {
    const input = isClearing ? [] : rows.map(({ layerId, displayField }) => ({ layerId: layerId as number, displayField }));
    try {
      // One at a time, so the first rejected key field stops the save with its own error.
      for (const row of rows.filter(needsKeyField)) await setKeyField(row.layerId as number, row.keyField);
      await saveConfig(input);
      onClose();
    } catch {
      // The hook already reported the error; keep the editor open for a retry.
    }
  };

  return (
    <div className={styles.editAssetListRoot}>
      <h3 className={styles.editAssetListTitle}>Edit Asset List</h3>

      <div className={styles.editAssetListBody}>
        <span className={styles.editAssetListIncludeLabel}>Include:</span>

        <div className={styles.editAssetListRowsScroll}>
          {rows.map((row) => {
            const fieldState = row.layerId === null ? undefined : fieldsByLayerId.get(row.layerId);
            // Each layer may appear once; a layer without a key field gets one picked below.
            const layerOptions = (layers ?? []).map((layer) => ({
              label: layer.name,
              value: layer.id,
              disabled: layer.id !== row.layerId && selectedLayerIds.includes(layer.id),
            }));
            const fieldOptions = (fieldState?.fields ?? []).map(({ name }) => ({ label: name, value: name }));
            const keyFieldOptions = fieldOptions.map((option) =>
              option.value === fieldState?.suggestedKeyField ? { ...option, label: `${option.label} (suggested)` } : option,
            );

            return (
              <div key={row.id} className={styles.editAssetListRowGroup}>
                <div className={styles.editAssetListRow}>
                  <Select
                    className={styles.editAssetListLayerSelect}
                    suffixIcon={<CaretDownFilled />}
                    placeholder="Select layer"
                    aria-label="Layer"
                    value={row.layerId ?? undefined}
                    onChange={(layerId?: number) => handleLayerChange(row.id, layerId ?? null)}
                    options={layerOptions}
                    loading={isLoadingLayers}
                    popupMatchSelectWidth={false}
                    allowClear
                  />

                  <span className={styles.editAssetListFieldLabel}>Display Field:</span>
                  <Select
                    className={styles.editAssetListFieldSelect}
                    suffixIcon={<CaretDownFilled />}
                    placeholder="Select field"
                    aria-label="Display field"
                    value={row.displayField || undefined}
                    onChange={(displayField: string) => handleFieldChange(row.id, displayField)}
                    disabled={row.layerId === null}
                    loading={fieldState?.isLoading}
                    options={fieldOptions}
                    popupMatchSelectWidth={false}
                  />

                  <button
                    type="button"
                    className={styles.editAssetListRemove}
                    aria-label="Remove or clear row"
                    onClick={() => handleRemoveRow(row.id)}
                    disabled={rows.length === 1 && row.layerId === null}
                  >
                    ×
                  </button>
                </div>

                <ConditionalRender visible={needsKeyField(row)}>
                  <div className={styles.editAssetListKeyRow}>
                    <span className={styles.editAssetListFieldLabel}>Key Field:</span>
                    <Select
                      className={styles.editAssetListFieldSelect}
                      suffixIcon={<CaretDownFilled />}
                      placeholder="Select unique field"
                      aria-label="Key field"
                      value={row.keyField || undefined}
                      onChange={(keyField: string) => handleKeyFieldChange(row.id, keyField)}
                      loading={fieldState?.isLoading}
                      options={keyFieldOptions}
                      popupMatchSelectWidth={false}
                    />
                  </div>
                  <span className={styles.editAssetListKeyHint}>Identifies each asset; every feature needs a unique value.</span>
                </ConditionalRender>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.editAssetListAddWrap}>
        <button type="button" className={styles.editAssetListAdd} aria-label="Add layer" onClick={handleAddRow}>
          +
        </button>
      </div>

      <div className={styles.editAssetListFooter}>
        <button type="button" className={styles.editAssetListSave} onClick={handleSave} disabled={!canSave}>
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default EditAssetListModal;
